/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { SRMSProvider, useSRMS } from './context/SRMSContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { TopHeader } from './components/layout/TopHeader';
import { LoginScreen } from './components/auth/LoginScreen';
import { PublicSite } from './components/public/PublicSite';
import { AgentAndroidApp } from './components/agent/AgentAndroidApp';
import { WebPortal } from './components/web/WebPortal';
import { SyncStatusModal, NotificationModal } from './components/common/HeaderModals';

const SRMSMainApp: React.FC = () => {
  const { isAuthenticated, deviceMode, authResolved } = useSRMS();
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  // Firebase restores the signed-in user asynchronously after a refresh.
  // Until it reports back we must NOT show the login screen, otherwise a
  // refresh looks identical to being logged out.
  if (!authResolved) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#0f172a] text-slate-300 gap-4">
        <div className="h-10 w-10 rounded-full border-2 border-slate-600 border-t-blue-500 animate-spin" />
        <p className="text-sm font-semibold tracking-wide">Signing you in…</p>
      </div>
    );
  }

  /**
   * WHAT scalesupport.co.in SHOWS.
   *
   *   /login  ->  the SRMS sign-in screen, with password recovery.
   *   /       ->  the company website, which carries its own sign-in window.
   *
   * Anybody signed in goes straight to the portal whatever the address, so a
   * refresh never drops an agent back onto the marketing page.
   *
   * The site's sign-in form calls the same `loginWithCredentials` as the
   * login screen does, so there is one way in and one place where sign-in is
   * decided. When it succeeds, `isAuthenticated` turns true and this
   * component swaps the page for the portal on the next render -- no
   * redirect, no reload, nothing to keep in step.
   *
   * Firebase Hosting already rewrites every path to index.html, so /login is
   * served without any further configuration.
   */
  if (!isAuthenticated) {
    const path = (window.location.pathname || '/').replace(/\/+$/, '').toLowerCase();
    const wantsLoginScreen = path === '/login' || path === '/srms';
    return wantsLoginScreen ? <LoginScreen /> : <PublicSite />;
  }

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-[#0f172a] text-slate-800 flex flex-col font-sans selection:bg-blue-600 selection:text-white">
      {/* Universal Top Header */}
      <TopHeader
        onOpenSyncModal={() => setIsSyncModalOpen(true)}
        onOpenNotifications={() => setIsNotificationModalOpen(true)}
      />

      {/* Dynamic Workspace: Android Mobile App for Field Agents vs Full Desktop Web Portal */}
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        {deviceMode === 'android' ? (
          <AgentAndroidApp />
        ) : (
          <WebPortal />
        )}
      </div>

      {/* Header Shared Modals */}
      <SyncStatusModal
        isOpen={isSyncModalOpen}
        onClose={() => setIsSyncModalOpen(false)}
      />
      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
      />
    </div>
  );
};

export default function App() {
  return (
    <ErrorBoundary>
      <SRMSProvider>
        <SRMSMainApp />
      </SRMSProvider>
    </ErrorBoundary>
  );
}
